import { Typography, AutoComplete, AutoCompleteProps, AutoCompleteOption } from '@mgmresorts/mgm-ui';
import React, { useContext, useState, useCallback, useEffect, ChangeEvent } from 'react';

import { StyledLinkContainer } from '../../../components/ui/Inputs/ManualAddressForm/styles';
import { NavigationLink } from '../../../components/ui/Link';
import { useContent } from '../../../hooks/useContent';
import { EventAttributes } from '../../../utils/analytics/types';
import { useEvent } from '../../../utils/analytics/useEvent';
import { SignUpFormContext } from '../SignUpFormContext';

const PredictionList = (props: {
  onPredictionSelected: (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    value: string,
    option: AutoCompleteOption | null,
  ) => void;
  error: string | null;
  placePredictions: google.maps.places.AutocompletePrediction[];
}) => {
  const { onPredictionSelected: onSelection, placePredictions, error } = props;
  const trackEvent = useEvent();
  const {
    isLoading,
    invalidFields,
    selectedPlaceId,
    setSelectedPlaceId,
    setAddressValue,
    onInputChange: onAddressSearch,
  } = useContext(SignUpFormContext);
  const { getContentByPath } = useContent();
  const [inputValue, setInputValue] = useState(() => {
    if (selectedPlaceId && placePredictions.length > 0) {
      const match = placePredictions.find(prediction => prediction.place_id === selectedPlaceId);
      return match?.description || '';
    }
    return '';
  });
  const [analyticsFieldErrors, setAnalyticsFieldErrors] = useState<string[]>([]);

  useEffect(() => {
    if (invalidFields.length > 0) {
      const eventAttributes = {} as EventAttributes;
      if (invalidFields.includes('address') && !analyticsFieldErrors.includes('address')) {
        eventAttributes.errorField = 'Address';
        eventAttributes.errorDisplayed = getContentByPath('shared.fields.address.location.error');
      }

      if (eventAttributes.errorField) {
        trackEvent(
          'identity – mlife - registration input error',
          'mlife registration input error',
          'mlifereginputerror',
          eventAttributes,
        );
      }
    }

    setAnalyticsFieldErrors(invalidFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidFields, invalidFields.length]);

  const getAddressOptions: AutoCompleteProps['getOptions'] = useCallback(async () => {
    return [];
  }, []);

  return (
    <AutoComplete
      id='address-autocomplete'
      testId='address-autocomplete'
      getOptions={getAddressOptions}
      debounce={500}
      inputThreshold={3}
      inputValue={inputValue}
      optionMode='sync'
      options={placePredictions.map(prediction => ({
        value: prediction.place_id,
        text: prediction.description,
      }))}
      searchIcon
      placeholder={getContentByPath('accountCreation.fields.address.prediction.insertPrediction')}
      onInputChange={value => {
        if (value === '') {
          setSelectedPlaceId('');
        }
        setInputValue(value);
        if (!selectedPlaceId && value.length >= 3) {
          onAddressSearch('address', value);
        }
      }}
      error={!!error}
      hint={error as string}
      onSelection={(event, value, option) => {
        onSelection(event, value, option);
      }}
      loading={isLoading}
      autoComplete='street-address'
      onClearSelection={() => {
        setSelectedPlaceId('');
        setInputValue('');
        setAddressValue('');
      }}
      name='address'
    />
  );
};

const PlacesPredictionsList = ({ onManualEntrySelection }: { onManualEntrySelection: () => void }) => {
  const { setSelectedPlaceId, selectedPlaceId, placePredictions, getCachedPlaceDetails } =
    useContext(SignUpFormContext);
  const [error, setError] = useState<string | null>(null);
  const { getContentByPath } = useContent();

  const getError = useCallback(async () => {
    if (selectedPlaceId) {
      const { address } = await getCachedPlaceDetails(selectedPlaceId);
      if (address?.city && address?.country && address?.street) {
        setError(null);
        return;
      }
      setError(getContentByPath('accountCreation.fields.address.prediction.error'));
      return;
    }
    setError(null);
  }, [getContentByPath, getCachedPlaceDetails, selectedPlaceId]);

  const onPredictionSelected = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>, value: string) => {
      setSelectedPlaceId(value);
    },
    [setSelectedPlaceId],
  );

  useEffect(() => {
    getError();
  }, [getError]);

  return (
    <>
      <PredictionList placePredictions={placePredictions} onPredictionSelected={onPredictionSelected} error={error} />
      <StyledLinkContainer>
        <Typography variant='body-regular-s'>
          <NavigationLink onClick={onManualEntrySelection} path='#'>
            {getContentByPath('accountCreation.fields.address.prediction.insertManually')}
          </NavigationLink>
        </Typography>
      </StyledLinkContainer>
    </>
  );
};

export default PlacesPredictionsList;
